import * as apiUser from 'api/endpoints/user/user.api';
import * as UserTypes from './user.types';
import { notificationErr } from 'views/components/notification';
import { userError } from 'utils/errorCodes';
import { messageError, messageSuccess } from 'views/components/message';
import * as clientKey from 'utils/constants';
import { TUpdateLanguagePick, TUpdateUserPick } from 'interfaces/user.interface';
import { ITranslate } from 'interfaces/general.interface';
import { ISAdminUserTenantState } from 'api/endpoints/superAdmin/superAdmin.response.interface';
import {
  IUpdateUserAPIBody,
  IloginDbUserAPIResponseData,
} from 'api/endpoints/user/user.response.interface';
import { Dispatch } from 'react';
import { TuserDispatchReducer } from './user.reducer';

export async function loginUserAction({
  dispatch,
  token,
  emailVerified,
  translate,
  logout,
}: {
  dispatch: Dispatch<TuserDispatchReducer>;
  token: string;
  emailVerified: boolean | undefined;
  translate: ITranslate;
  logout: Function;
}): Promise<boolean> {
  dispatch({ type: UserTypes.SET_IS_LOADING, payload: true });
  let user: IloginDbUserAPIResponseData | null = null;
  try {
    const userFetch = await apiUser.loginDbUserAPI({ token });
    if (userFetch.response.status === 404 || !userFetch.data) {
      logout();
      return false;
    }

    if (userFetch.response.status === 403) {
      window.location.replace(getUrlToRedirectId() as string);
      return false;
    }

    if (userFetch.response.status === 200) {
      user = userFetch.data;
      dispatch({
        type: UserTypes.LOGIN_USER,
        payload: userFetch.data,
      });
      return true;
    }
    if (userFetch.response?.status !== 503) {
      notificationErr(userError.login_user_001, translate);
    }
    return false;
  } catch (err) {
    notificationErr(userError.login_user_002, translate);
    return false;
  } finally {
    dispatch({ type: UserTypes.SET_IS_LOADING, payload: false });
    if (user && user.enabled === false && emailVerified === true) {
      try {
        const body: IUpdateUserAPIBody = {
          filter: { id: user.id },
          update: { enabled: true },
        };
        const verifyFetch = await apiUser.updateDbUserAPI({ body, token });
        if (verifyFetch.response.status === 200) {
          dispatch({
            type: UserTypes.VERIFY_EMAIL,
            payload: true,
          });
        }
      } catch (error) {
        messageError('Error al verificar el usuario.');
        notificationErr(userError.login_user_003, translate);
      }
    }
  }
}

export async function updateUserAction({
  dispatch,
  dbUserId,
  data,
  token,
  translate,
}: {
  dispatch: Dispatch<TuserDispatchReducer>;
  dbUserId: string;
  data: TUpdateUserPick;
  token: string;
  translate: ITranslate;
}) {
  const body: IUpdateUserAPIBody = { filter: { id: dbUserId }, update: data };
  try {
    const fetch = await apiUser.updateDbUserAPI({ body, token });

    if (fetch.response.status === 403) {
      messageError('No tienes permisos.');
      notificationErr(userError.update_user_002, translate);
      return false;
    }

    if (fetch.response.status === 200) {
      messageSuccess('Usuario actualizado correctamente.');
      dispatch({
        type: UserTypes.UPDATE_USER,
        payload: data,
      });
      return true;
    }
    return false;
  } catch (err) {
    messageError('Error al actualizar el usuario.');
    notificationErr(userError.update_user_001, translate);
    return false;
  }
}

export async function updateUserAvatarAction({
  dispatch,
  userId,
  image,
  token,
  translate,
}: {
  dispatch: Dispatch<TuserDispatchReducer>;
  userId: string;
  image: File;
  token: string;
  translate: ITranslate;
}) {
  try {
    const fetch = await apiUser.updateUserAvatarAPI({
      userId,
      image,
      token,
    });
    if (fetch.response.status === 200) {
      messageSuccess('Avatar de usuario actualizado correctamente.');
      dispatch({
        type: UserTypes.UPDATE_USER_AVATAR,
        payload: fetch.data.url,
      });
      return true;
    }
    messageError('Error al actualizar el avatar de usuario.');
    notificationErr(userError.update_user_avatar_002, translate);
    return false;
  } catch (err) {
    messageError('Error al actualizar el avatar de usuario.');
    notificationErr(userError.update_user_avatar_001, translate);
    return false;
  }
}

export async function updateLanguageAction({
  dispatch,
  dbUserId,
  languageId,
  token,
  translate,
}: {
  dispatch: Dispatch<TuserDispatchReducer>;
  dbUserId: string;
  languageId: string;
  token: string;
  translate: ITranslate;
}) {
  const body: IUpdateUserAPIBody = {
    filter: { id: dbUserId },
    update: { i18n_lang_id: languageId } as TUpdateLanguagePick,
  };
  try {
    const updateLanguageFetch = await apiUser.updateDbUserAPI({ body, token });
    if (updateLanguageFetch.response.status === 200) {
      dispatch({ type: UserTypes.UPDATE_LANGUAGE, payload: languageId });
      return true;
    } else {
      notificationErr(userError.update_language_002, translate);
      return false;
    }
  } catch (err) {
    notificationErr(userError.update_language_001, translate);
    return false;
  }
}

export function selectUserSuperAdminAction({
  dispatch,
  user,
}: {
  dispatch: Dispatch<TuserDispatchReducer>;
  user: ISAdminUserTenantState;
}) {
  if (!user) return false;
  dispatch({
    type: UserTypes.LOGIN_COMPANY_USER_SUPER_ADMIN,
    payload: user,
  });
  return true;
}

///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////

const getUrlToRedirectId = () => {
  if (
    process.env.REACT_APP_NODE_ENV === clientKey.clientLocalKey ||
    !process.env.REACT_APP_NODE_ENV
  ) {
    return clientKey.clientLocal;
  } else if (process.env.REACT_APP_NODE_ENV === clientKey.clientProdKey) {
    return clientKey.clientProd;
  } else if (process.env.REACT_APP_NODE_ENV === clientKey.clientDevKey) {
    return clientKey.clientDev;
  } else if (process.env.REACT_APP_NODE_ENV === clientKey.clientStagingKey) {
    return clientKey.clientStaging;
  }
};
